import React, { createContext, useContext, useId, useMemo, useState } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import MuiSwitch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import Close from '@mui/icons-material/Close';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { DayParting, Option } from './types';
import { DAY_PARTING_DAYS, includeExcludeOptions } from './constants';
import { normalizeZips } from '../../../utils/conversionFunctions';
import { VirtualizedListbox } from './VirtualizedListbox';

/*
 * The building blocks every criteria section is made of. Two layers:
 *
 *   layout  — CriteriaSection (one bordered group), FieldGrid (fluid columns), EntryCard (one
 *             row of a repeatable list), HelpTip and FieldLabel.
 *   fields  — SelectSimple, MultiSelect, Input, Switch, IncludeExcludeField … each takes a
 *             `help` that renders as an info tooltip beside its label.
 *
 * Nothing here knows about CriteriaState; the sections in ./sections wire state to these.
 *
 * Responsiveness comes from intrinsic sizing (auto-fill grids and wrapping flex rows) rather
 * than viewport breakpoints: the panel is rendered full-width on detail pages, inside a 45vh
 * scroll box on Sequences and inside builder tabs, so the viewport says little about how much
 * room the panel actually has.
 */

/** Number inputs keep the legacy look: no spinners, and the wheel never nudges a value. */
export const NO_SPINNER_SX = {
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
} as const;

/** Keeps the scroll wheel from nudging a number field's value while the page scrolls past it. */
export const blurOnWheel = (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur();

const DEFAULT_GROUP = 'Options';
const CREATE_GROUP = 'New';

/* ------------------------------------------------------------------------------------------ */
/* Layout                                                                                      */
/* ------------------------------------------------------------------------------------------ */

// pre-line keeps the line breaks in multi-paragraph copy (the suppression Level tip is one bullet per line).
const TOOLTIP_SLOT_PROPS = {
  tooltip: { sx: { maxWidth: 'min(26rem, 90vw)', textAlign: 'left', whiteSpace: 'pre-line' } },
} as const;

interface HelpTipProps {
  /** What the tooltip explains, for the button's accessible name ("About Min Age"). */
  label: string;
  title: React.ReactNode;
}

/**
 * The (i) beside a label. A real button so it is reachable by keyboard and announced;
 * describeChild keeps "About …" as its name instead of the whole tooltip text.
 */
export function HelpTip({ label, title }: HelpTipProps) {
  return (
    <Tooltip describeChild title={title} placement='top' slotProps={TOOLTIP_SLOT_PROPS}>
      <IconButton
        size='small'
        aria-label={`About ${label}`}
        sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
      >
        <InfoOutlined sx={{ fontSize: '1rem' }} />
      </IconButton>
    </Tooltip>
  );
}

/** On every field label, so a layout that labels its columns once (day parting) can hide them per row. */
export const FIELD_LABEL_CLASS = 'criteria-field-label';

interface FieldLabelProps {
  htmlFor?: string;
  id?: string;
  children: React.ReactNode;
  help?: React.ReactNode;
}

/**
 * Label above a control, with its help tip inline. The row has a fixed minimum height so a
 * label with a tip and one without still line their controls up across a grid row.
 */
export function FieldLabel({ htmlFor, id, children, help }: FieldLabelProps) {
  return (
    <Box
      className={FIELD_LABEL_CLASS}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minHeight: 24, mb: 0.5 }}
    >
      <FormLabel htmlFor={htmlFor} id={id} sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.primary' }}>
        {children}
      </FormLabel>
      {help && typeof children === 'string' ? <HelpTip label={children} title={help} /> : null}
    </Box>
  );
}

/**
 * Whether the section being rendered holds configuration. The tab sets it around each section
 * (see SectionSlot) so CriteriaSection can mark configured groups without every section
 * passing a prop through.
 */
const SectionStatusContext = createContext({ configured: false });
export const SectionStatusProvider = SectionStatusContext.Provider;

const SECTION_CONTAINER = 'criteria-section';
/** A criteria section wide enough to lay Include/Exclude criteria out as name | mode | values rows. */
const SECTION_WIDE = `@container ${SECTION_CONTAINER} (min-width: 40rem)`;

interface CriteriaSectionProps {
  title: string;
  /** Explains the whole group; shown as a help tip beside the title. */
  help?: React.ReactNode;
  /** Sits at the far end of the header, for the section's own controls (Add, sort …). */
  actions?: React.ReactNode;
  /** Small marker beside the title — the summary uses it to name the tab a section lives in. */
  badge?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * One criteria group. A bordered box with its own header so neighbouring groups can never
 * run into each other — each tab and the summary are a stack of these and nothing else.
 */
export function CriteriaSection({ title, help, actions, badge, children }: CriteriaSectionProps) {
  const headingId = useId();
  const { configured } = useContext(SectionStatusContext);
  return (
    <Box
      component='section'
      aria-labelledby={headingId}
      sx={(theme) => ({
        border: 1,
        borderRadius: 1,
        bgcolor: 'background.paper',
        minWidth: 0,
        // A configured group gets a primary edge, so what is set up stands out while scanning a tab.
        borderColor: configured ? theme.alpha((theme.vars ?? theme).palette.primary.main, 0.6) : 'divider',
        borderLeftWidth: configured ? 4 : 1,
      })}
    >
      <Box
        sx={(theme) => ({
          bgcolor: configured ? theme.alpha((theme.vars ?? theme).palette.primary.main, 0.07) : 'transparent',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          columnGap: 1,
          rowGap: 0.5,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          borderTopLeftRadius: 'inherit',
          borderTopRightRadius: 'inherit',
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Typography id={headingId} variant='subtitle2' component='h3' sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {help && <HelpTip label={title} title={help} />}
          {configured && (
            <Chip
              size='small'
              color='primary'
              variant='outlined'
              icon={<CheckCircleOutlined />}
              label='Configured'
              sx={{ ml: 0.5, height: 22 }}
            />
          )}
          {badge}
        </Box>
        {actions && (
          <Box sx={{ ml: 'auto', display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>{actions}</Box>
        )}
      </Box>
      {/* A size container, so a field can lay itself out for the section's width rather than the
          viewport's — see IncludeExcludeField. */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          containerType: 'inline-size',
          containerName: SECTION_CONTAINER,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

interface FieldGridProps {
  /** Narrowest a column may get before the grid drops a column. */
  min?: string;
  children: React.ReactNode;
}

/**
 * Fluid columns: as many `min`-wide columns as fit, each stretching to share the row. A wide
 * page gets three or four, a narrow side panel gets one, with no breakpoints to keep in sync.
 */
export function FieldGrid({ min = '15rem', children }: FieldGridProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        alignItems: 'start',
        gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${min}), 1fr))`,
      }}
    >
      {children}
    </Box>
  );
}

/** Makes a FieldGrid child span every column — for a wide control in an otherwise narrow grid. */
export function FullRow({ children }: { children: React.ReactNode }) {
  return <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>{children}</Box>;
}

interface EntryCardProps {
  title: string;
  onRemove: () => void;
  removeLabel?: string;
  /** Extra header controls, left of the remove button. */
  actions?: React.ReactNode;
  /** Outlines the whole entry in the error colour, for a problem that is about the entry rather than one field. */
  error?: boolean;
  children: React.ReactNode;
}

/** One entry of a repeatable list (a pre-ping, a previous registration …), as an inset well. */
export function EntryCard({
  title,
  onRemove,
  removeLabel = 'Remove this entry',
  actions,
  error = false,
  children,
}: EntryCardProps) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: error ? 'error.main' : 'divider',
        borderRadius: 1,
        bgcolor: 'zuul.inset',
        minWidth: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1.5, pr: 0.5, py: 0.5 }}>
        <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
          {title}
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          {actions}
          <Tooltip title={removeLabel}>
            <IconButton size='small' color='error' aria-label={removeLabel} onClick={onRemove}>
              <Close fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ px: 1.5, pb: 1.5 }}>{children}</Box>
    </Box>
  );
}

const ENTRY_FIELD_FLEX = {
  narrow: '0 1 9rem',
  medium: '1 1 12rem',
  wide: '2 1 16rem',
} as const;

/**
 * The fields of one entry, sized by what they hold: a selector or a number stays narrow, a
 * list of domains or affiliates takes the room. Equal grid columns used to truncate the lists
 * to "api-local.w…" while a Min field sat half empty. Wraps when the entry is narrow.
 */
export function EntryFields({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>{children}</Box>;
}

export function EntryField({ size, children }: { size: keyof typeof ENTRY_FIELD_FLEX; children: React.ReactNode }) {
  return <Box sx={{ flex: ENTRY_FIELD_FLEX[size], minWidth: 0 }}>{children}</Box>;
}

/** The quiet line a repeatable list shows before its first entry is added. */
export function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic' }}>
      {children}
    </Typography>
  );
}

/* ------------------------------------------------------------------------------------------ */
/* Fields                                                                                      */
/* ------------------------------------------------------------------------------------------ */

interface SelectSimpleProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Option[];
  placeholder?: string;
  error?: string | undefined;
  help?: React.ReactNode;
  disabled?: boolean;
  /** Drop the visible label (the control is still named by it) — for a select inside a row that already has one. */
  hideLabel?: boolean;
}

/**
 * Native `select` rather than MUI's own popup menu: these panes render dozens of selects into a
 * dense grid, the option lists are plain strings, and a native control keeps `optgroup`,
 * label-for association and the platform picker exactly as they were.
 */
export function SelectSimple({
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
  help,
  disabled,
  hideLabel = false,
}: SelectSimpleProps) {
  const id = useId();
  const uniqueOptions = options.filter(
    (option, index, arr) => index === arr.findIndex((o) => o.value === option.value)
  );

  return (
    <Box sx={{ minWidth: 0 }}>
      {!hideLabel && (
        <FieldLabel htmlFor={id} help={help}>
          {label}
        </FieldLabel>
      )}
      <TextField
        select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        error={Boolean(error)}
        helperText={error}
        slotProps={{ select: { native: true }, htmlInput: hideLabel ? { 'aria-label': label } : undefined }}
      >
        <option value=''>{placeholder || 'Select'}</option>
        {uniqueOptions.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </TextField>
    </Box>
  );
}

interface SelectSimpleWithGroupsProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Option[];
  placeholder?: string;
  groupsOrder?: string[];
  disabled?: boolean;
  error?: string | undefined;
  onBlur?: () => void;
  help?: React.ReactNode;
}

function useGroupedOptions(options: Option[], groupsOrder?: string[]) {
  const groups = useMemo(() => {
    const byGroup = new Map<string, Option[]>();
    const seen = new Set<string>();

    for (const opt of options) {
      if (seen.has(opt.value)) continue;
      seen.add(opt.value);
      const key = opt.group ?? DEFAULT_GROUP;
      if (!byGroup.has(key)) byGroup.set(key, []);
      byGroup.get(key)!.push(opt);
    }
    return byGroup;
  }, [options]);

  const orderedGroups = useMemo(() => {
    const keys = Array.from(groups.keys());
    if (!groupsOrder?.length) return keys;
    const orderSet = new Set(groupsOrder);
    const pref = keys.filter((k) => orderSet.has(k)).sort((a, b) => groupsOrder.indexOf(a) - groupsOrder.indexOf(b));
    const rest = keys.filter((k) => !orderSet.has(k));
    return [...pref, ...rest];
  }, [groups, groupsOrder]);

  return { groups, orderedGroups };
}

export function SelectSimpleWithGroups({
  label,
  value,
  onChange,
  options,
  placeholder,
  groupsOrder,
  disabled,
  error,
  onBlur,
  help,
}: SelectSimpleWithGroupsProps) {
  const id = useId();
  const { groups, orderedGroups } = useGroupedOptions(options, groupsOrder);

  return (
    <Box sx={{ minWidth: 0 }}>
      <FieldLabel htmlFor={id} help={help}>
        {label}
      </FieldLabel>
      <TextField
        select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        error={Boolean(error)}
        helperText={error}
        slotProps={{ select: { native: true } }}
      >
        <option value=''>{placeholder || 'Select'}</option>
        {orderedGroups.map((groupKey) => {
          const groupOptions = groups.get(groupKey);
          if (!groupOptions || groupOptions.length === 0) return null;

          return (
            <optgroup key={groupKey} label={groupKey}>
              {groupOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </optgroup>
          );
        })}
      </TextField>
    </Box>
  );
}

/** 'include' / 'INCLUDE' → 'Include'; anything else (including '') passes through. */
export function normalizeIncludeExclude(value: string | null | undefined): string {
  const lower = value?.toLowerCase();
  if (lower === 'include') return 'Include';
  if (lower === 'exclude') return 'Exclude';
  return value || '';
}

/** True once an Include/Exclude selector has a mode chosen, whatever its casing. */
export function isIncludeExclude(value: string | null | undefined): boolean {
  const normalized = normalizeIncludeExclude(value);
  return normalized === 'Include' || normalized === 'Exclude';
}

interface SelectIncludeExcludeProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
  help?: React.ReactNode;
  hideLabel?: boolean;
}

export function SelectIncludeExclude({ label, value, onChange, error, help, hideLabel }: SelectIncludeExcludeProps) {
  return (
    <SelectSimple
      label={label}
      value={normalizeIncludeExclude(value)}
      onChange={onChange}
      options={includeExcludeOptions}
      placeholder='Select'
      error={error}
      help={help}
      hideLabel={hideLabel}
    />
  );
}

interface IncludeExcludeFieldProps {
  label: string;
  help?: React.ReactNode;
  mode: string;
  onModeChange: (v: string) => void;
  modeError?: string | undefined;
  /**
   * The values control. Rendered beside the selector only once Include or Exclude is chosen.
   * Leave out for a criterion that is the mode alone (Null Source New Users).
   */
  children?: React.ReactNode;
}

/**
 * The criteria's most common shape — "Include/Exclude these values" — as one labelled row:
 * a narrow mode selector with the values control beside it. The row wraps the values under
 * the selector when the panel is narrow.
 *
 * The values control appears only once a mode is chosen, rather than holding an invisible
 * slot open: the hidden slot is what used to leave gaps that made unrelated rows read as one.
 */
interface CriteriaRowProps {
  label: string;
  help?: React.ReactNode;
  /** Caps the control column, so a lone number or select doesn't stretch across a wide section. */
  controlMaxWidth?: string;
  children: React.ReactNode;
}

/**
 * One criterion as a table row. In a wide section its name sits in a fixed column with the
 * controls beside it, so a stack of rows reads down like a table; in a narrow one the name
 * sits above. The control should be labelled by `label` itself (`hideLabel`), not by this.
 */
export function CriteriaRow({ label, help, controlMaxWidth, children }: CriteriaRowProps) {
  return (
    <Box
      sx={{
        minWidth: 0,
        [SECTION_WIDE]: {
          display: 'grid',
          gridTemplateColumns: '13rem minmax(0, 1fr)',
          columnGap: 2,
          alignItems: 'start',
          [`& > .${FIELD_LABEL_CLASS}`]: { minHeight: 40, mb: 0 },
        },
      }}
    >
      <FieldLabel help={help}>{label}</FieldLabel>
      <Box sx={{ minWidth: 0, maxWidth: controlMaxWidth }}>{children}</Box>
    </Box>
  );
}

export function IncludeExcludeField({
  label,
  help,
  mode,
  onModeChange,
  modeError,
  children,
}: IncludeExcludeFieldProps) {
  const active = isIncludeExclude(mode);
  return (
    <CriteriaRow label={label} help={help}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', minWidth: 0 }}>
        <Box sx={{ flex: '0 0 8.5rem' }}>
          <SelectIncludeExclude label={label} value={mode} onChange={onModeChange} error={modeError} hideLabel />
        </Box>
        {active && children && <Box sx={{ flex: '1 1 14rem', minWidth: 0 }}>{children}</Box>}
      </Box>
    </CriteriaRow>
  );
}

/** Several CriteriaRows (or Include/Exclude fields) in one section: divided, in their usual order. */
export function CriteriaRows({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        // A rule above every row but the first. CSS rather than Stack's divider, which can't see
        // into a fragment of rows that only some source types render.
        '& > * + *': { pt: 1.5, borderTop: 1, borderColor: 'divider' },
      }}
    >
      {children}
    </Box>
  );
}

interface MultiSelectProps {
  label?: string;
  /** Names the control when there is no visible label (e.g. inside an IncludeExcludeField). */
  ariaLabel?: string;
  value: string[] | null | undefined;
  onChange: (val: string[]) => void;
  options: Option[];
  placeholder?: string | undefined;
  groupsOrder?: string[];
  allowCreate?: boolean;
  disabled?: boolean;
  error?: string | undefined;
  helperText?: string;
  isLoading?: boolean;
  onBlur?: () => void;
  help?: React.ReactNode;
}

const MULTISELECT_TAG_LIMIT = 24;

const matchLabelOrValue = createFilterOptions<Option>({ stringify: (option) => `${option.label} ${option.value}` });

export function MultiSelect({
  label,
  ariaLabel,
  value,
  onChange,
  options,
  placeholder = 'Select...',
  groupsOrder,
  allowCreate = false,
  disabled,
  error,
  helperText,
  isLoading = false,
  onBlur,
  help,
}: MultiSelectProps) {
  const id = useId();
  const safeValue = useMemo(() => value || [], [value]);
  const { groups, orderedGroups } = useGroupedOptions(options, groupsOrder);

  // Autocomplete starts a new group whenever the group key changes, so the ordering has to be
  // baked into the option array rather than applied while the popup renders.
  const orderedOptions = useMemo(
    () => orderedGroups.flatMap((groupKey) => groups.get(groupKey) ?? []),
    [groups, orderedGroups]
  );

  const optionsMap = useMemo(() => new Map(orderedOptions.map((opt) => [opt.value, opt])), [orderedOptions]);

  // Values created by typing have no matching option; they still need to show as a chip.
  const selectedOptions = useMemo(
    () => safeValue.map((v) => optionsMap.get(v) ?? { label: v, value: v }),
    [safeValue, optionsMap]
  );

  return (
    <Box sx={{ minWidth: 0 }}>
      {label && (
        <FieldLabel htmlFor={id} help={help}>
          {label}
        </FieldLabel>
      )}
      <Autocomplete
        multiple
        id={id}
        options={orderedOptions}
        value={selectedOptions}
        disabled={disabled}
        loading={isLoading}
        // Selecting keeps the popup open and drops the option from the list, the way the
        // criteria selects have always behaved; autoHighlight makes Enter take the top match.
        disableCloseOnSelect
        filterSelectedOptions
        autoHighlight
        // Small chips fit roughly twice as many values on a line. A very long list shows its
        // first MULTISELECT_TAG_LIMIT values and a "+N more" while idle, and all of them on focus.
        limitTags={MULTISELECT_TAG_LIMIT}
        getLimitTagsText={(more) => `+${more} more`}
        slotProps={{ chip: { size: 'small' } }}
        groupBy={(option) => option.group ?? DEFAULT_GROUP}
        getOptionLabel={(option) => option.label}
        getOptionDisabled={(option) => Boolean(option.disabled)}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        // A greyed-out option is kept out even if something other than a pointer picks it; when
        // that was the only change, the caller hears nothing.
        onChange={(_event, next) => {
          const values = next.filter((opt) => !opt.disabled).map((opt) => opt.value);
          if (values.length === safeValue.length && values.every((v, i) => v === safeValue[i])) return;
          onChange(values);
        }}
        onBlur={onBlur}
        filterOptions={(opts, params) => {
          const filtered = matchLabelOrValue(opts, params);
          const typed = params.inputValue.trim();
          // Free text only becomes an option once nothing matches — otherwise Enter would
          // create a near-duplicate of an entry that is already in the list.
          if (allowCreate && typed && filtered.length === 0 && !safeValue.includes(typed)) {
            filtered.push({ label: `Create "${typed}"`, value: typed, group: CREATE_GROUP });
          }
          return filtered;
        }}
        noOptionsText='No results found.'
        slots={{ listbox: VirtualizedListbox }}
        // The virtualized listbox consumes these as data, not as elements.
        renderOption={(props, option) => [props, option] as React.ReactNode}
        renderGroup={(params) => params as unknown as React.ReactNode}
        renderInput={(params) => (
          <TextField
            {...params}
            slotProps={{
              ...params.slotProps,
              htmlInput: { ...params.slotProps.htmlInput, ...(ariaLabel && !label ? { 'aria-label': ariaLabel } : {}) },
            }}
            placeholder={safeValue.length === 0 ? placeholder : undefined}
            error={Boolean(error)}
            helperText={error || helperText}
          />
        )}
      />
    </Box>
  );
}

interface TimeSelectProps {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  error?: string | undefined;
}

function formatTime(h: number, m: number): string {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const TIME_OPTIONS: Option[] = Array.from({ length: (24 * 60) / 15 }, (_, i) => {
  const mins = i * 15;
  const value = formatTime(Math.floor(mins / 60), mins % 60);
  return { label: toAmPm(value), value };
});

export function TimeSelect({ label, value, onChange, error }: TimeSelectProps) {
  return (
    <SelectSimple
      label={label}
      value={value || ''}
      onChange={onChange}
      options={TIME_OPTIONS}
      placeholder='Select...'
      error={error}
    />
  );
}

interface SwitchProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Optional info icon with hover/focus tooltip (e.g. compliance copy). */
  tooltip?: React.ReactNode;
}

export function Switch({ id, label, checked, onChange, tooltip }: SwitchProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
      <FormControlLabel
        control={<MuiSwitch id={id} size='small' checked={checked} onChange={(e) => onChange(e.target.checked)} />}
        label={label}
        sx={{ mr: 0, ml: 0, gap: 1, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
      />
      {tooltip ? <HelpTip label={label} title={tooltip} /> : null}
    </Box>
  );
}

interface InputProps {
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string | undefined;
  helperText?: string;
  help?: React.ReactNode;
  onBlur?: () => void;
  min?: number;
  /** Rendered before the value, e.g. a currency sign. */
  startAdornment?: React.ReactNode;
  /** Drop the visible label (the input is still named by it) — for an input inside a CriteriaRow. */
  hideLabel?: boolean;
}

export function Input({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  disabled,
  error,
  helperText,
  help,
  onBlur,
  min,
  startAdornment,
  hideLabel = false,
}: InputProps) {
  const id = useId();
  return (
    <Box sx={{ minWidth: 0 }}>
      {!hideLabel && (
        <FieldLabel htmlFor={id} help={help}>
          {label}
        </FieldLabel>
      )}
      <TextField
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        error={Boolean(error)}
        helperText={error || helperText}
        sx={NO_SPINNER_SX}
        slotProps={{
          htmlInput: { min, onWheel: blurOnWheel, ...(hideLabel ? { 'aria-label': label } : {}) },
          ...(startAdornment
            ? { input: { startAdornment: <InputAdornment position='start'>{startAdornment}</InputAdornment> } }
            : {}),
        }}
      />
    </Box>
  );
}

interface TextAreaProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  placeholder?: string;
  error?: string | undefined;
  helperText?: React.ReactNode;
  help?: React.ReactNode;
  minRows?: number;
  maxRows?: number;
  /**
   * A fixed height in rows, with no auto-sizing. Auto-sizing measures a hidden copy of the
   * whole value on every render, which gets expensive for very long values (a zip list).
   */
  rows?: number;
  /** Visible label off; the textarea is still named by `label`. */
  hideLabel?: boolean;
}

/** Multi-line text that grows with its content up to `maxRows`, so pasted lists stay readable. */
export function TextArea({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  helperText,
  help,
  minRows = 3,
  maxRows = 12,
  rows,
  hideLabel = false,
}: TextAreaProps) {
  const id = useId();
  return (
    <Box sx={{ minWidth: 0 }}>
      {!hideLabel && (
        <FieldLabel htmlFor={id} help={help}>
          {label}
        </FieldLabel>
      )}
      <TextField
        id={id}
        multiline
        {...(rows ? { rows } : { minRows, maxRows })}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur ? (e) => onBlur(e.target.value) : undefined}
        placeholder={placeholder}
        error={Boolean(error)}
        helperText={error || helperText}
        slotProps={{ htmlInput: hideLabel ? { 'aria-label': label } : undefined }}
      />
    </Box>
  );
}

interface ReadOnlyValueProps {
  label: string;
  value: React.ReactNode;
  help?: React.ReactNode;
}

/**
 * A live figure read from Redis (a current cap count) next to the field that sets its limit.
 * A disabled input swallows mouse events and implies it could be enabled, so this is a plain
 * field-shaped box instead.
 */
export function ReadOnlyValue({ label, value, help }: ReadOnlyValueProps) {
  const id = useId();
  return (
    <Box sx={{ minWidth: 0 }}>
      <FieldLabel id={id} help={help}>
        {label}
      </FieldLabel>
      <Box
        role='status'
        aria-labelledby={id}
        sx={{
          height: 40,
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'action.hover',
          color: 'text.secondary',
          fontSize: '0.875rem',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

interface ZipTextareaStatsProps {
  value: string;
  onChange: (v: string) => void;
  error?: string;
  onBlur?: () => void;
  /** Visible label off; the textarea is still named "Zip Codes". */
  hideLabel?: boolean;
}

/** Rows a long zip list shows until it is expanded. */
const ZIP_ROWS_COLLAPSED = 4;

export function ZipTextareaStats({ value, onChange, error, onBlur, hideLabel = false }: ZipTextareaStatsProps) {
  const [expanded, setExpanded] = useState(false);
  const validZips = value?.split(/\s+/).filter((zip) => zip.trim().length === 5 && !isNaN(Number(zip)));

  const count = validZips?.length || 0;
  const unique = new Set(validZips || []).size;
  // About six zips fit a line, so this is "more than the collapsed rows can show".
  const isLong = count > ZIP_ROWS_COLLAPSED * 6;

  return (
    <TextArea
      label='Zip Codes'
      hideLabel={hideLabel}
      value={value}
      onChange={(v) => onChange(normalizeZips(v))}
      onBlur={onBlur ? () => onBlur() : undefined}
      placeholder='12345 23456 34567'
      error={error}
      // Collapsed, a long list is a fixed four-line box: auto-sizing would lay out every zip
      // (16k on some offers) just to arrive at the same height.
      {...(isLong && !expanded ? { rows: ZIP_ROWS_COLLAPSED } : { minRows: 3, maxRows: 20 })}
      helperText={
        <Box
          component='span'
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontVariantNumeric: 'tabular-nums' }}
        >
          {`${count.toLocaleString('en-US')} zips · ${unique.toLocaleString('en-US')} unique`}
          {isLong && (
            <Link component='button' type='button' variant='caption' onClick={() => setExpanded((open) => !open)}>
              {expanded ? 'Show less' : 'Show all'}
            </Link>
          )}
        </Box>
      }
    />
  );
}

/* ------------------------------------------------------------------------------------------ */
/* Day parting conversions                                                                     */
/* ------------------------------------------------------------------------------------------ */

export interface DayPartingEntry {
  restrictionDay: string;
  restriction: string;
  restrictionStart?: string;
  restrictionEnd?: string;
}

export function toAmPm(time24?: string): string {
  if (!time24) return '';
  // Expecting HH:MM
  const match = /^(\d{1,2}):(\d{2})$/.exec(time24.trim());
  if (!match) return time24; // already AM/PM or invalid; pass through
  let h = parseInt(match[1] || '0', 10);
  const m = match[2];
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${suffix}`;
}

function fromAmPm(time12?: string): string {
  if (!time12) return '';
  const str = time12.trim();
  const isAmPm = /(AM|PM)$/i.test(str);
  if (!isAmPm) {
    // Might already be 24h HH:MM
    const basic = /^(\d{1,2}):(\d{2})$/.exec(str);
    return basic ? `${(basic[1] || '0').padStart(2, '0')}:${basic[2] || '00'}` : str;
  }
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(str);
  if (!m) return str;
  let h = parseInt(m[1] || '0', 10);
  const min = m[2] || '00';
  const mer = (m[3] || 'AM').toUpperCase();
  if (mer === 'AM') {
    if (h === 12) h = 0;
  } else {
    if (h !== 12) h += 12;
  }
  return `${String(h).padStart(2, '0')}:${min}`;
}

export function convertDayPartingToArray(dayParting: DayParting | string | null | undefined): DayPartingEntry[] {
  const entries: DayPartingEntry[] = [];

  // Handle different input types
  let safeDayParting: DayParting = {};

  if (typeof dayParting === 'string') {
    try {
      safeDayParting = JSON.parse(dayParting) || {};
    } catch (e) {
      console.warn('Failed to parse dayHourRestrictions JSON:', e);
      safeDayParting = {};
    }
  } else {
    safeDayParting = dayParting || {};
  }

  DAY_PARTING_DAYS.forEach((day) => {
    const restriction = safeDayParting[day as keyof DayParting];
    const entry: DayPartingEntry = {
      restrictionDay: day,
      restriction: restriction?.restriction || 'Available',
    };

    if (restriction?.restriction === 'Partially Restricted') {
      entry.restrictionStart = fromAmPm(restriction.restrictionStart);
      entry.restrictionEnd = fromAmPm(restriction.restrictionEnd);
    }

    entries.push(entry);
  });

  return entries;
}

export function convertArrayToDayParting(entries: DayPartingEntry[]): string {
  const dayParting: DayParting = {};

  DAY_PARTING_DAYS.forEach((day) => {
    const entry = entries.find((e) => e.restrictionDay === day);
    const isPartial = entry?.restriction === 'Partially Restricted';

    dayParting[day as keyof DayParting] = {
      restriction: entry?.restriction || 'Available',
      restrictionStart: isPartial ? toAmPm(entry?.restrictionStart || '') : '',
      restrictionEnd: isPartial ? toAmPm(entry?.restrictionEnd || '') : '',
    };
  });

  return JSON.stringify(dayParting);
}
